export { apiClient } from './client';
export { reportsApi } from './reports';
export { photosApi } from './photos';
export { costCatalogApi } from './costCatalog';
export { visionApi } from './vision';
export { voiceApi } from './voice';
export { inspectionsApi } from './inspections';
export { agreementsApi } from './agreements';
export { availabilityApi, bookingApi, inspectorApi } from './booking';

import { apiClient } from './client';
import { ApiResponse, Appointment, Contact, AnalyticsData, RepairRequest, User } from '@/types';

// Payments — Stripe Connect Express is post-launch. The legacy
// `paymentsApi.createPaymentIntent` wrapper was unused (zero call sites)
// and would need a different signature for Connect anyway, so it's dropped
// here. Backend `routes/payments.ts` and the `payments` table are kept
// intact for the Connect integration.

export const schedulingApi = {
  getAppointments: async () => {
    return apiClient.get<ApiResponse<Appointment[]>>('/appointments');
  },

  createAppointment: async (data: Partial<Appointment>) => {
    return apiClient.post<ApiResponse<Appointment>>('/appointments', data);
  },

  updateAppointment: async (id: string, data: Partial<Appointment>) => {
    return apiClient.put<ApiResponse<Appointment>>(`/appointments/${id}`, data);
  },

  cancelAppointment: async (id: string) => {
    return apiClient.delete<ApiResponse<void>>(`/appointments/${id}`);
  },

  confirmAppointment: async (id: string) => {
    return apiClient.post<ApiResponse<{
      appointment: { id: string; status: string; inspectionId: string };
      agreement: {
        id: string;
        status: string;
        sentAt: string | null;
        expiresAt: string | null;
        signingUrl: string;
        emailSent: boolean;
        emailError: string | null;
      };
      inspection: { id: string; status: string };
    }>>(`/appointments/${id}/confirm`, {});
  },
};

export const crmApi = {
  getContacts: async () => {
    return apiClient.get<ApiResponse<Contact[]>>('/contacts');
  },

  createContact: async (data: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => {
    return apiClient.post<ApiResponse<Contact>>('/contacts', data);
  },

  updateContact: async (id: string, data: Partial<Contact>) => {
    return apiClient.put<ApiResponse<Contact>>(`/contacts/${id}`, data);
  },

  deleteContact: async (id: string) => {
    return apiClient.delete<ApiResponse<void>>(`/contacts/${id}`);
  },

  sendEmail: async (contactId: string, templateType: string, customData?: any) => {
    return apiClient.post<ApiResponse<void>>('/contacts/send-email', {
      contactId,
      templateType,
      customData,
    });
  },
};

export const usersApi = {
  getProfile: async () => {
    return apiClient.get<ApiResponse<User>>('/users/profile');
  },

  updateProfile: async (data: Partial<User>) => {
    return apiClient.put<ApiResponse<User>>('/users/profile', data);
  },

  uploadLogo: async (file: File): Promise<User> => {
    const resp = await apiClient.uploadFile<ApiResponse<User>>('/users/logo', file);
    if (!resp.success || !resp.data) throw new Error('Failed to upload logo');
    return resp.data;
  },

  deleteLogo: async () => {
    return apiClient.delete<ApiResponse<User>>('/users/logo');
  },

  uploadHeadshot: async (file: File): Promise<User> => {
    const resp = await apiClient.uploadFile<ApiResponse<User>>('/users/headshot', file);
    if (!resp.success || !resp.data) throw new Error('Failed to upload headshot');
    return resp.data;
  },

  deleteHeadshot: async () => {
    return apiClient.delete<ApiResponse<User>>('/users/headshot');
  },

  checkSlug: async (slug: string) => {
    return apiClient.get<ApiResponse<{ available: boolean; suggestion: string | null }>>(
      `/users/slug-check?slug=${encodeURIComponent(slug)}`
    );
  },
};

export const analyticsApi = {
  getAnalytics: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return apiClient.get<ApiResponse<AnalyticsData>>(`/analytics?${params.toString()}`);
  },
};

export const repairRequestsApi = {
  createRepairRequest: async (data: Omit<RepairRequest, 'id' | 'createdAt' | 'updatedAt' | 'status'>) => {
    return apiClient.post<ApiResponse<RepairRequest>>('/repair-requests', data);
  },

  getReportRepairRequests: async (reportId: string) => {
    return apiClient.get<ApiResponse<RepairRequest[]>>(`/repair-requests/report/${reportId}`);
  },
};
